import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import { Box, Button, LinearProgress, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import RCBarChart from '../../components/charts/recharts/rcBarChart';
import MSelect from '../../components/controls/MSelect';
import CityStateDetailPopup from './citystatecheck/CityStateDetailPopup';

/** A market-type option loaded from the server. */
interface Choice {
    id: string;
    name: string;
}

/** Row shape returned by `CityStateCheck/Filter`. */
interface CityStateRow {
    marketType?: string;
    isExist?: boolean;
    /** Count of orphaned `smallspider_event` (VenueId = 0) rows in this City/State group. */
    eventCount?: number;
    [key: string]: unknown;
}

/** Per-MarketType aggregate used to feed the {@link RCBarChart}. */
interface CityStateMarketStat {
    /** Market type identifier (x-axis category). */
    id: string;
    /** Total City/State groups found for this market type. */
    totalGroups: number;
    /** Groups missing from `venue_city_states` (`isExist = false`). */
    missingCount: number;
    /** Groups already present in `venue_city_states` (`isExist = true`). */
    existingCount: number;
    /** Sum of orphaned event counts across every City/State group for this market type. */
    totalEventCount: number;
}

/** Tooltip payload entry supplied by Recharts for a single series at the hovered bar. */
interface ChartTooltipPayloadEntry {
    dataKey?: string;
    name?: string;
    value?: number;
    fill?: string;
    payload?: CityStateMarketStat;
}

/** Props Recharts passes into a custom `<Tooltip content>` component. */
interface ChartTooltipProps {
    active?: boolean;
    payload?: ChartTooltipPayloadEntry[];
    label?: string;
}

/**
 * Custom bar-chart tooltip: shows the per-series breakdown (missing/existing groups) plus the
 * total orphaned event count for the hovered MarketType, so operators can gauge urgency without
 * opening the detail popup.
 */
const ChartTooltip: React.FC<ChartTooltipProps> = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    const stat = payload[0]?.payload;
    return (
        <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, boxShadow: 2 }}>
            <Typography variant="subtitle2">{label}</Typography>
            {payload.map((row) => (
                <Typography key={row.dataKey} variant="body2" sx={{ color: row.fill }}>
                    {row.name}: {row.value}
                </Typography>
            ))}
            <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                Total events: {stat?.totalEventCount ?? 0}
            </Typography>
        </Box>
    );
};

/** Props accepted by {@link CityStateCheckView}. */
interface CityStateCheckViewProps {
    /** Market-type choices loaded from the server and shared with the other tabs. */
    marketTypeChoices?: Choice[];
}

/**
 * City/State-Check tab of the Inventory Drop Analysis page.
 *
 * Surfaces `smallspider_event` rows with `VenueId = 0` (unmapped venues) grouped by
 * market type, city, and state, cross-referenced against `venue_city_states`.
 * A market-type filter narrows the summary counts, and a Scan-Level-style bar chart
 * visualises the groups per market type. Clicking a bar opens
 * {@link CityStateDetailPopup} — scoped to that bar's market type — for inline
 * editing and bulk-merge.
 *
 * Mirrors the header layout (title + Refresh) and keep-alive behavior of the
 * Scan-Level tab defined in `index.tsx`.
 */
const CityStateCheckView: React.FC<CityStateCheckViewProps> = ({ marketTypeChoices }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [marketType, setMarketType] = useState<string>('');
    const [rows, setRows] = useState<CityStateRow[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [popupOpen, setPopupOpen] = useState<boolean>(false);
    const [refreshKey, setRefreshKey] = useState<number>(0);
    /** Market type scoping the popup; set only by `handleBarClick` so opening it never touches — or reloads — the chart's own `marketType` filter. */
    const [popupMarketType, setPopupMarketType] = useState<string>('');

    const fetchSummary = useCallback(() => {
        setIsLoading(true);
        dataProvider
            .getList('CityStateCheck', {
                filter: { MarketType_eq: marketType || '' },
                pagination: { page: 1, perPage: 5000 },
                sort: { field: 'id', order: 'ASC' },
            })
            .then((response: { data?: CityStateRow[] }) => {
                setRows(Array.isArray(response?.data) ? response.data : []);
            })
            .catch((err: Error) => {
                notify(`Failed to load City/State summary: ${err?.message}`, { type: 'error', autoHideDuration: 5000 });
            })
            .finally(() => setIsLoading(false));
    }, [dataProvider, notify, marketType]);

    useEffect(() => {
        fetchSummary();
    }, [fetchSummary, refreshKey]);

    const handleRefresh = (): void => setRefreshKey((k) => k + 1);
    const handleMarketTypeChange = (value: string): void => setMarketType(value);
    const handleMerged = (): void => fetchSummary();

    /**
     * Clicking a bar drills into that market type's City/State groups. This only opens the
     * popup (scoped via `popupMarketType`) — it must never touch the chart's own `marketType`
     * filter state, since doing so would re-trigger `fetchSummary` and reload/redraw the chart
     * on every click.
     */
    const handleBarClick = (id: string): void => {
        if (!id) return;
        setPopupMarketType(id);
        setPopupOpen(true);
    };

    const totalGroups = rows.length;
    const missingCount = rows.filter((r) => !r.isExist).length;

    /** Aggregates the flat City/State group rows into per-MarketType counts for the bar chart. */
    const chartData = useMemo<CityStateMarketStat[]>(() => {
        const byMarket = new Map<string, CityStateMarketStat>();
        rows.forEach((row) => {
            const key = row.marketType || 'Unknown';
            const stat = byMarket.get(key) || { id: key, totalGroups: 0, missingCount: 0, existingCount: 0, totalEventCount: 0 };
            stat.totalGroups += 1;
            if (row.isExist) stat.existingCount += 1;
            else stat.missingCount += 1;
            stat.totalEventCount += Number(row.eventCount) || 0;
            byMarket.set(key, stat);
        });
        return Array.from(byMarket.values()).sort((a, b) => b.totalGroups - a.totalGroups);
    }, [rows]);

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="h5">City/State Check</Typography>
                <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={isLoading}>
                    Refresh
                </Button>
                <Box sx={{ maxWidth: 180, minWidth: 140 }}>
                    <MSelect
                        label="Market Type"
                        value={marketType}
                        choices={marketTypeChoices || []}
                        onChange={handleMarketTypeChange as any}
                    />
                </Box>
            </Box>

            {isLoading && <LinearProgress color="secondary" />}

            {!isLoading && (
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                    {totalGroups === 0
                        ? 'No orphaned City/State groups found for the selected market type.'
                        : `${totalGroups} City/State group(s) found — ${missingCount} missing from venue_city_states.`}
                </Typography>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Chart shows venues missing City/State information (unmapped for more than 36 hours).
            </Typography>

            {!isLoading && chartData.length > 0 && (
                <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Click a bar to manage that market type&apos;s City/State groups.
                    </Typography>
                    {/* Scan-Level-style grouped bar chart: City/State groups per market type. Each bar is clickable and opens the Manage City/State popup scoped to that market type. */}
                    <Box className="city-state-chart-clickable" sx={{ overflowX: 'auto' }}>
                        <RCBarChart
                            data={chartData}
                            width={Math.max(800, chartData.length * 120)}
                            height={500}
                            xAxis={{
                                dataKey: 'id'
                            }}
                            yAxis={{
                                position: 'insideLeft',
                                label: {
                                    angle: -90,
                                    dx: -10,
                                }
                            }}
                            legend={{ verticalAlign: 'top', height: 60, iconType: 'square' }}
                            barSize={chartData.length > 10 ? 30 : 50}
                            showTotal
                            customTooltip={ChartTooltip}
                            yGap={2}
                            seriesList={[
                                {
                                    dataKey: 'missingCount',
                                    name: 'Missing (not in venue_city_states)',
                                    fill: '#d32f2f',
                                    stroke: '#d32f2f',
                                    strokeWidth: 0
                                },
                                {
                                    dataKey: 'existingCount',
                                    name: 'Existing',
                                    fill: '#43a047',
                                    stroke: '#43a047',
                                    strokeWidth: 0
                                },
                            ]}
                            onClick={(data) => handleBarClick(data.activeLabel)}
                            barGap={0}
                            legendStyle={{
                                fontSize: '12px',
                                fontWeight: 'bold',
                                paddingTop: '0px',
                                marginTop: '0px'
                            }}
                        />
                    </Box>
                </>
            )}

            <CityStateDetailPopup
                open={popupOpen}
                onClose={() => setPopupOpen(false)}
                marketType={popupMarketType}
                onMerged={handleMerged}
            />
        </Box>
    );
};

export default CityStateCheckView;
